import { useEffect } from 'react';

const DEFAULT_TITLE = 'ModelMap - Multi-Provider Model Deployment Explorer';
const DEFAULT_DESCRIPTION = 'Compare model offerings, regions, pricing, and governance controls across AI providers and platforms.';
const DEFAULT_URL = 'https://modelmap.app';

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface PageMetaOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  robots?: string;
  jsonLd?: Record<string, unknown> | null;
  breadcrumbs?: BreadcrumbItem[];
}

function setMetaContent(selector: string, content: string) {
  const meta = document.querySelector(selector);
  if (meta) meta.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector("link[rel='canonical']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function setHreflang(canonicalUrl: string) {
  // Remove existing hreflang links
  document.querySelectorAll("link[rel='alternate'][hreflang]").forEach((el) => el.remove());
  
  // Add English hreflang
  const enLink = document.createElement('link');
  enLink.setAttribute('rel', 'alternate');
  enLink.setAttribute('hreflang', 'en');
  enLink.setAttribute('href', canonicalUrl);
  document.head.appendChild(enLink);
  
  // Add x-default hreflang
  const defaultLink = document.createElement('link');
  defaultLink.setAttribute('rel', 'alternate');
  defaultLink.setAttribute('hreflang', 'x-default');
  defaultLink.setAttribute('href', canonicalUrl);
  document.head.appendChild(defaultLink);
}

function resetHreflang() {
  document.querySelectorAll("link[rel='alternate'][hreflang]").forEach((el) => el.remove());
  
  // Restore default static hreflang from index.html
  const enLink = document.createElement('link');
  enLink.setAttribute('rel', 'alternate');
  enLink.setAttribute('hreflang', 'en');
  enLink.setAttribute('href', DEFAULT_URL + '/');
  document.head.appendChild(enLink);
  
  const defaultLink = document.createElement('link');
  defaultLink.setAttribute('rel', 'alternate');
  defaultLink.setAttribute('hreflang', 'x-default');
  defaultLink.setAttribute('href', DEFAULT_URL + '/');
  document.head.appendChild(defaultLink);
}

function normalizeCanonicalPath(path: string) {
  return path === '/' ? path : path.replace(/\/+$/, '');
}

function setJsonLd(schema: Record<string, unknown> | null, breadcrumbs?: BreadcrumbItem[]) {
  // Remove existing JSON-LD scripts
  const existingProfile = document.getElementById('profile-jsonld');
  const existingBreadcrumb = document.getElementById('breadcrumb-jsonld');
  
  if (!schema && !breadcrumbs) {
    existingProfile?.remove();
    existingBreadcrumb?.remove();
    return;
  }

  // Set main schema (SoftwareApplication, WebPage, etc.)
  if (schema) {
    const script = existingProfile ?? document.createElement('script');
    script.setAttribute('id', 'profile-jsonld');
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(schema);
    if (!existingProfile) {
      document.head.appendChild(script);
    }
  } else {
    existingProfile?.remove();
  }

  // Set breadcrumb schema
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${DEFAULT_URL}${item.path}`,
      })),
    };
    
    const script = existingBreadcrumb ?? document.createElement('script');
    script.setAttribute('id', 'breadcrumb-jsonld');
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(breadcrumbSchema);
    if (!existingBreadcrumb) {
      document.head.appendChild(script);
    }
  } else {
    existingBreadcrumb?.remove();
  }
}

/**
 * Hook to manage page-level meta tags for SEO.
 * Updates document title, description, OG tags, Twitter tags, canonical URL,
 * and optional JSON-LD structured data. Resets to defaults on unmount.
 */
export function usePageMeta(options: PageMetaOptions) {
  useEffect(() => {
    const { title, description, canonicalPath, ogTitle, ogDescription, robots, jsonLd, breadcrumbs } = options;

    const canonicalUrl = canonicalPath
      ? `${DEFAULT_URL}${normalizeCanonicalPath(canonicalPath)}`
      : DEFAULT_URL;

    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDescription = ogDescription || description;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:title"]', resolvedOgTitle);
    setMetaContent('meta[property="og:description"]', resolvedOgDescription);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', resolvedOgTitle);
    setMetaContent('meta[name="twitter:description"]', resolvedOgDescription);
    setMetaContent('meta[name="twitter:url"]', canonicalUrl);
    setMetaContent('meta[name="robots"]', robots || 'index, follow');

    if (jsonLd !== undefined || breadcrumbs !== undefined) {
      setJsonLd(jsonLd || null, breadcrumbs);
    }

    return () => {
      document.title = DEFAULT_TITLE;
      setCanonical(DEFAULT_URL);
      resetHreflang();
      setMetaContent('meta[name="description"]', DEFAULT_DESCRIPTION);
      setMetaContent('meta[property="og:title"]', DEFAULT_TITLE);
      setMetaContent('meta[property="og:description"]', DEFAULT_DESCRIPTION);
      setMetaContent('meta[property="og:url"]', DEFAULT_URL);
      setMetaContent('meta[name="twitter:title"]', DEFAULT_TITLE);
      setMetaContent('meta[name="twitter:description"]', DEFAULT_DESCRIPTION);
      setMetaContent('meta[name="twitter:url"]', DEFAULT_URL);
      setMetaContent('meta[name="robots"]', 'index, follow');
      setJsonLd(null);
    };
  }, [
    options.title,
    options.description,
    options.canonicalPath,
    options.ogTitle,
    options.ogDescription,
    options.robots,
    options.jsonLd,
    options.breadcrumbs,
  ]);
}
